import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Clock, ArrowRight, AlertTriangle } from "lucide-react";
import { fetchSalesScorecard } from "../lib/api";
import { useAuth } from "../lib/AuthProvider";
import { Skeleton } from "./ui/skeleton";

/**
 * Dashboard mini scorecard. Compact single-row layout per rep, inside a
 * fixed-height scroll container so a growing team never stretches the
 * Dashboard. Clicking any row jumps to the full report in Weekly Recap
 * (`/recap#scorecard-<contact_id>`). Admin + dispatcher only.
 */

function relDays(iso) {
  if (!iso) return { label: "never", tone: "text-rose-300" };
  const now = new Date();
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return { label: "—", tone: "text-zinc-500" };
  const days = Math.round((now - then) / (1000 * 60 * 60 * 24));
  if (days < 1) return { label: "today",  tone: "text-emerald-300" };
  if (days === 1) return { label: "1d",   tone: "text-emerald-300" };
  if (days < 7)  return { label: `${days}d`, tone: "text-emerald-300" };
  if (days < 14) return { label: `${days}d`, tone: "text-amber-300" };
  if (days < 30) return { label: `${days}d`, tone: "text-amber-400" };
  return { label: `${days}d`, tone: "text-rose-300" };
}

function RepRow({ rep }) {
  const hasBook = rep.assigned_count > 0;
  const pct = hasBook ? Math.round((rep.touched_count / rep.assigned_count) * 100) : 0;
  const last = relDays(rep.last_touch_at);
  const barTone =
    pct >= 80 ? "bg-emerald-500"
    : pct >= 50 ? "bg-amber-500"
    : pct > 0  ? "bg-rose-500/80"
    : "bg-zinc-700";

  return (
    <Link
      to={`/recap#scorecard-${rep.contact_id}`}
      data-testid={`scorecard-row-${rep.contact_id}`}
      className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-zinc-900/60 transition-colors group"
    >
      <span className="text-sm text-zinc-100 font-medium truncate w-[88px] shrink-0">
        {rep.name}
      </span>
      {hasBook ? (
        <>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
              <div
                className={`h-full ${barTone} transition-all`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-[10px] font-mono text-zinc-400 shrink-0 tabular-nums">
              {rep.touched_count}/{rep.assigned_count}
            </span>
          </div>
          {rep.stale_count > 0 ? (
            <span
              className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-mono text-rose-300"
              title={`${rep.stale_count} assigned shops not touched in 30+ days`}
            >
              <AlertTriangle className="w-3 h-3" />
              {rep.stale_count}
            </span>
          ) : (
            <span className="w-[22px] shrink-0" />
          )}
          <span
            className={`text-xs font-mono shrink-0 w-[42px] text-right tabular-nums ${last.tone}`}
            title={rep.last_touch_at || "never touched"}
          >
            {last.label}
          </span>
        </>
      ) : (
        <span className="text-[11px] font-mono text-zinc-600 italic flex-1">
          no accounts assigned
        </span>
      )}
    </Link>
  );
}

export function SalesScorecardCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user || !["admin", "dispatcher"].includes(user.role)) return;
    fetchSalesScorecard().then(setData).catch((e) => setErr(e.message || "error"));
  }, [user]);

  if (!user || !["admin", "dispatcher"].includes(user.role)) return null;

  const repCount = data?.reps?.length || 0;

  return (
    <div className="panel p-5 flex flex-col" data-testid="sales-scorecard-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-emerald-300" />
          <h3 className="text-sm font-medium tracking-tight">
            Sales rep scorecard
          </h3>
          {repCount > 0 && (
            <span className="text-[10px] font-mono text-zinc-500">
              · {repCount} rep{repCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <Link
          to="/recap#scorecard"
          data-testid="scorecard-view-full"
          className="text-xs text-emerald-400 hover:text-emerald-300 font-mono inline-flex items-center gap-1"
        >
          Full report
          <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {/* Column legend */}
      <div className="grid grid-cols-[88px_1fr_22px_42px] gap-3 px-3 pb-1.5 text-[9px] font-mono uppercase tracking-wider text-zinc-600 border-b border-zinc-800">
        <span>Rep</span>
        <span>Touched / assigned</span>
        <span className="text-right">!</span>
        <span className="text-right">Last</span>
      </div>

      {err ? (
        <div className="text-xs text-rose-300 py-3">
          Couldn't load scorecard — {err}
        </div>
      ) : !data ? (
        <div className="space-y-2 pt-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-9 rounded-md bg-zinc-900/50" />
          ))}
        </div>
      ) : repCount === 0 ? (
        <div className="text-sm text-zinc-500 py-3 px-3">
          No sales reps in Contacts yet.
        </div>
      ) : (
        // Fixed scroll window so a growing team never stretches the Dashboard
        <div
          data-testid="scorecard-scroll"
          className="max-h-[280px] overflow-y-auto -mx-1 px-1 pt-1 scrollbar-thin"
        >
          {data.reps.map((rep) => (
            <RepRow key={rep.contact_id} rep={rep} />
          ))}
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-zinc-800 flex items-center gap-1.5 text-[10px] font-mono text-zinc-500">
        <Clock className="w-3 h-3" />
        Last 30d · click a row for details
      </div>
    </div>
  );
}
